<template>
    <div class="annual-fee-container w-full" :class="getContainerClasses()">
        <span :class="getLabelClasses()" v-if="showLabel">Annual Fee:</span>
        <div :class="getContentClasses()" v-if="!item.user_managed_annual_fee">
            <!-- initial annual fee active -->
            <span
                class="text-xs font-normal text-slate-500"
                v-if="
                    item.card &&
                    item.card.initial_annual_fee !== undefined &&
                    item.card.initial_annual_fee !== null &&
                    item.initial_annual_fee_active
                "
            >
                <span class="ml-2 md:block md:ml-0">
                    <span
                        class="text-xs lg:text-sm text-slate-800 dark:text-slate-100"
                        :class="{
                            'line-through': item.card_status === 'closed',
                        }"
                    >
                        ${{ item.card.initial_annual_fee }}
                    </span>
                    <span class="hidden md:block">
                        ${{ item.card.annual_fee }}
                        after the first year
                    </span>
                </span>
            </span>
            <span
                class="ml-2 sm:ml-0 text-xs lg:text-sm"
                :class="{
                    'line-through': item.card_status === 'closed',
                }"
                v-if="
                    item.card &&
                    item.card.annual_fee !== undefined &&
                    item.card.annual_fee !== null &&
                    !item.initial_annual_fee_active
                "
            >
                ${{ item.card.annual_fee }}
            </span>
            <span
                v-if="
                    item.card &&
                    item.date_opened &&
                    item.annual_fee_due_date &&
                    item.annual_fee_due_date.within_30_days &&
                    item.card.annual_fee > 0 &&
                    item.card_status === 'open'
                "
                class="text-amber-500"
            >
                <span
                    class="text-xs hidden lg:flex items-center whitespace-nowrap"
                >
                    <div class="flex items-center">
                        Due: {{ formatDate(item.annual_fee_due_date.fulldate) }}

                        <Tooltip
                            text="This is an estimate for your planning purposes. Consult with your issuer for the exact date."
                        />
                    </div>
                </span>
            </span>
        </div>
        <div
            v-if="item.user_managed_annual_fee"
            :class="getUserManagedClasses()"
        >
            <span class="text-sm md:block">
                <span
                    class="text-slate-800 dark:text-slate-100 text-xs lg:text-sm"
                    :class="{
                        'text-slate-800 dark:text-slate-100 line-through':
                            item.card_status === 'closed',
                    }"
                >
                    ${{ item.annual_fee }}
                </span>
            </span>
        </div>
    </div>
</template>
<script>
import dayjs from 'dayjs';
import Tooltip from '@/components/tooltip.vue';

export default {
    props: {
        item: {
            type: Object,
        },
        showLabel: {
            type: Boolean,
            default: true,
        },
        alignment: {
            type: String,
            default: 'start', // 'start', 'center', 'end'
            validator: (value) => ['start', 'center', 'end'].includes(value),
        },
    },
    components: {
        Tooltip,
    },

    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return dayjs(dateString).format('MM/YYYY');
        },
        getContainerClasses() {
            // Base classes for the container
            const baseClasses = [];

            // When alignment is specified (e.g., in wallet table), use simpler layout
            if (this.alignment === 'end') {
                baseClasses.push('flex', 'flex-col', 'items-end', 'text-right');
            } else if (this.alignment === 'center') {
                baseClasses.push(
                    'flex',
                    'flex-col',
                    'items-center',
                    'text-center'
                );
            } else if (this.alignment === 'start') {
                baseClasses.push(
                    'flex',
                    'flex-col',
                    'items-start',
                    'text-left'
                );
            } else {
                // Default responsive behavior when no specific alignment is set
                baseClasses.push(
                    'grid',
                    'grid-cols-12',
                    'gap-1',
                    'text-left',
                    'md:flex',
                    'md:flex-col',
                    'md:text-center',
                    'xl:justify-start',
                    '2xl:justify-center',
                    'xl:mx-3'
                );
            }

            return baseClasses;
        },
        getContentClasses() {
            // Classes for the main content div
            if (this.alignment === 'end') {
                return ['flex', 'flex-col', 'w-full', 'text-right'];
            } else if (this.alignment === 'center') {
                return ['flex', 'flex-col', 'w-full', 'text-center'];
            } else if (this.alignment === 'start') {
                return ['flex', 'flex-col', 'w-full', 'text-left'];
            } else {
                // Default responsive behavior
                return [
                    'flex',
                    'flex-col',
                    'w-full',
                    'md:w-auto',
                    'text-left',
                    'col-span-7',
                    'md:col-span-12',
                ];
            }
        },
        getUserManagedClasses() {
            // Classes for user managed annual fee div
            if (this.alignment === 'end') {
                return ['text-right'];
            } else if (this.alignment === 'center') {
                return ['text-center'];
            } else if (this.alignment === 'start') {
                return ['text-left'];
            } else {
                // Default behavior
                return ['text-left'];
            }
        },
        getLabelClasses() {
            // Classes for the label span
            const baseClasses = [
                'text-xs',
                'font-semibold',
                'whitespace-nowrap',
            ];

            if (this.alignment === 'end') {
                baseClasses.push('w-full', 'text-right');
            } else if (this.alignment === 'center') {
                baseClasses.push('w-full', 'text-center');
            } else if (this.alignment === 'start') {
                baseClasses.push('w-full', 'text-left');
            } else {
                // Default responsive behavior
                baseClasses.push(
                    'xl:hidden',
                    'w-full',
                    'md:w-auto',
                    'col-span-5',
                    'md:col-span-12'
                );
            }

            return baseClasses;
        },
    },
};
</script>
<style lang="scss" scoped>
.annual-fee-label-trigger {
    &:hover {
        .annual-fee-label {
            display: block;
        }
    }
}
.annual-fee-label {
    display: none;
    width: 200px;
    padding: 6px 12px;
    color: #fff;
    background-color: #1c293d;
    border-radius: 4px;
    position: absolute;
    bottom: 110%;
    left: 50%;
    margin-left: -100px;
}
</style>
